import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Send, MessageSquare, CheckCheck, Smile, Paperclip, Ban, ShieldCheck, MoreVertical, Image, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/Avatar';

// Phase 88 — Map a contact's role to the Avatar component's tone palette.
function avatarToneFor(role) {
  const r = (role || '').toLowerCase();
  if (r.includes('coach')) return 'staff';
  if (r.includes('admin') || r.includes('owner')) return 'admin';
  return 'student';
}

const EMOJIS = ['😀','😂','🥰','😎','🤩','🔥','💪','⚽','🏆','👏','👍','❤️','🎯','✅','🙏','😊','🤔','😅','💯','🥅','🎉','👋','🌟','💰','📋','📱','🍽️','🏃','⏰','🔔'];

export default function EmployeeChat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [blockedIds, setBlockedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const fetchContacts = useCallback(async () => {
    try {
      const [cRes, bRes] = await Promise.all([
        api.get('/chat/contacts'),
        api.get('/chat/blocked').catch(() => ({ data: [] }))
      ]);
      setContacts(cRes.data);
      setBlockedIds(bRes.data || []);
      if (cRes.data.length > 0 && !selectedContact) setSelectedContact(cRes.data[0]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const fetchMessages = useCallback(async () => {
    if (!selectedContact) return;
    try {
      const res = await api.get(`/chat/messages/${selectedContact.id}`);
      setMessages(res.data);
      api.put(`/chat/messages/${selectedContact.id}/read`).catch(() => {});
    } catch (err) { console.error(err); }
  }, [selectedContact]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { if (!selectedContact) return; const i = setInterval(fetchMessages, 5000); return () => clearInterval(i); }, [selectedContact, fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!msg.trim() || !selectedContact || sending) return;
    if (blockedIds.includes(selectedContact.id)) { toast.error('This user is blocked'); return; }
    setSending(true);
    try {
      const res = await api.post('/chat/messages', { receiver_id: selectedContact.id, text: msg.trim() });
      setMessages(prev => [...prev, res.data]);
      setMsg('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handleEmojiClick = (emoji) => { setMsg(prev => prev + emoji); };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedContact) return;
    if (file.size > 5000000) { toast.error('Max 5MB'); return; }
    if (blockedIds.includes(selectedContact.id)) { toast.error('This user is blocked'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await api.post('/chat/attachment', {
          file: ev.target.result,
          file_name: file.name,
          file_type: file.type.startsWith('image/') ? 'image' : 'file',
          receiver_id: selectedContact.id
        });
        setMessages(prev => [...prev, res.data]);
        toast.success(`${file.name} sent`);
      } catch (err) { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleBlock = async (contactId) => {
    try {
      await api.post(`/chat/block/${contactId}`);
      setBlockedIds(prev => [...prev, contactId]);
      toast.success('User blocked');
    } catch (err) { toast.error('Failed'); }
  };

  const handleUnblock = async (contactId) => {
    try {
      await api.delete(`/chat/block/${contactId}`);
      setBlockedIds(prev => prev.filter(id => id !== contactId));
      toast.success('User unblocked');
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 rounded-xl" /></div>;

  const isBlocked = selectedContact ? blockedIds.includes(selectedContact.id) : false;

  return (
    <div className="space-y-4" data-testid="employee-chat-page">
      <div><h1 className="text-xl font-display font-semibold">Messages</h1><p className="text-sm text-muted-foreground">Chat with students and staff</p></div>

      <div className="grid md:grid-cols-3 gap-4" style={{ height: 'calc(100vh - 250px)' }}>
        {/* Contacts */}
        <Card className="md:col-span-1 overflow-hidden">
          <CardContent className="p-0">
            <div className="p-3 border-b space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase">Contacts</p>
              <Input placeholder="Search contacts..." value={contactSearch} onChange={e => setContactSearch(e.target.value)} className="h-8 text-xs" />
            </div>
            <div className="overflow-y-auto max-h-[500px]">
              {contacts.filter(c => !contactSearch || c.name?.toLowerCase().includes(contactSearch.toLowerCase())).map(c => {
                const tone = avatarToneFor(c.role);
                const ringColor = tone === 'staff' ? 'ring-emerald-400' : tone === 'admin' ? 'ring-purple-400' : 'ring-blue-400';
                const blocked = blockedIds.includes(c.id);
                return (
                  <button key={c.id} onClick={() => setSelectedContact(c)} className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-muted/50 transition-colors ${selectedContact?.id === c.id ? 'bg-primary/5 border-l-2 border-primary' : ''} ${blocked ? 'opacity-50' : ''}`} data-testid={`chat-contact-${c.id}`}>
                    <Avatar src={c.photo_url || c.photo} name={c.name} size={36} online={c.online} tone={tone} ring={`ring-2 ${ringColor}`} testId={`chat-contact-avatar-${c.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${blocked ? 'line-through' : ''}`}>{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.role || 'User'}{blocked ? ' • Blocked' : c.last_message ? ` • ${c.last_message.slice(0, 20)}...` : ''}</p>
                    </div>
                    {c.unread_count > 0 && !blocked && <Badge className="bg-primary text-primary-foreground text-[10px] h-5 min-w-[20px] justify-center">{c.unread_count}</Badge>}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="md:col-span-2 flex flex-col overflow-hidden">
          {selectedContact ? (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-3">
                  <Avatar src={selectedContact.photo_url || selectedContact.photo} name={selectedContact.name} size={36} online={selectedContact.online} tone={avatarToneFor(selectedContact.role)} ring="ring-2 ring-white dark:ring-slate-800" testId="chat-header-avatar" />
                  <div>
                    <p className="text-sm font-medium" data-testid="chat-header-name">{selectedContact.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedContact.role}{selectedContact.online ? ' • Online' : ''}{isBlocked ? ' • Blocked' : ''}</p>
                  </div>
                </div>
                {/* Block/Unblock menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isBlocked ? (
                      <DropdownMenuItem onClick={() => handleUnblock(selectedContact.id)}><ShieldCheck className="h-4 w-4 mr-2 text-emerald-600" /> Unblock User</DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => handleBlock(selectedContact.id)} className="text-destructive"><Ban className="h-4 w-4 mr-2" /> Block User</DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
                {isBlocked && <div className="text-center py-4"><Badge variant="destructive" className="text-xs"><Ban className="h-3 w-3 mr-1" /> This user is blocked. Unblock to send messages.</Badge></div>}
                {messages.length === 0 && !isBlocked && <p className="text-sm text-muted-foreground text-center py-12">No messages yet. Start the conversation!</p>}
                {messages.map(m => {
                  const isMine = m.sender_id === user?.id;
                  const hasAttachment = m.attachment;
                  return (
                    <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3 py-2 rounded-xl ${isMine ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                        {!isMine && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{m.sender_name}</p>}
                        {hasAttachment && m.attachment.type === 'image' ? (
                          <div className="mb-1">
                            <img src={m.attachment.data} alt={m.attachment.name} className="max-w-full rounded-lg max-h-48 object-cover" />
                            <p className="text-[10px] mt-1 opacity-70">{m.attachment.name}</p>
                          </div>
                        ) : hasAttachment ? (
                          <div className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${isMine ? 'bg-white/10' : 'bg-muted'}`}>
                            <FileText className="h-5 w-5 flex-shrink-0" />
                            <div className="min-w-0"><p className="text-xs font-medium truncate">{m.attachment.name}</p><p className="text-[10px] opacity-60">Attachment</p></div>
                          </div>
                        ) : null}
                        {(!hasAttachment || m.text !== `📎 ${m.attachment?.name}`) && <p className="text-sm">{m.text}</p>}
                        <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : ''}`}>
                          <span className="text-[9px] opacity-60">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {isMine && <CheckCheck className={`h-3 w-3 ${m.read ? 'text-blue-300' : 'opacity-50'}`} />}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input Bar */}
              {!isBlocked && (
                <div className="flex items-center gap-2 p-3 border-t">
                  {/* Emoji Picker */}
                  <Popover>
                    <PopoverTrigger asChild><Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0"><Smile className="h-5 w-5 text-amber-500" /></Button></PopoverTrigger>
                    <PopoverContent className="w-64 p-2" side="top" align="start">
                      <div className="grid grid-cols-6 gap-1">
                        {EMOJIS.map(e => <button key={e} onClick={() => handleEmojiClick(e)} className="h-8 w-8 flex items-center justify-center text-lg hover:bg-muted rounded-md transition-colors">{e}</button>)}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Attachment */}
                  <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <Paperclip className="h-5 w-5 text-blue-500" />}
                  </Button>
                  <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={handleFileUpload} />

                  {/* Text Input */}
                  <Input value={msg} onChange={e => setMsg(e.target.value)} placeholder="Type a message..." className="flex-1" onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()} />
                  <Button onClick={handleSend} disabled={!msg.trim() || sending} className="bg-primary h-9 w-9 p-0 flex-shrink-0"><Send className="h-4 w-4" /></Button>
                </div>
              )}
              {isBlocked && (
                <div className="flex items-center justify-center gap-2 p-3 border-t bg-red-50 dark:bg-red-950/10">
                  <Ban className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-600">You blocked this user.</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUnblock(selectedContact.id)}><ShieldCheck className="h-3 w-3 mr-1" /> Unblock</Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground"><MessageSquare className="h-8 w-8 mr-2 opacity-30" /><p className="text-sm">Select a contact to start chatting</p></div>
          )}
        </Card>
      </div>
    </div>
  );
}
